import { captureException } from '@sentry/vue';
import { ref } from 'vue';
import { hideNotification, showNotification } from './notification';
import { lang, lockScrolling } from './utils';

export const shareModalVisible = ref(false);
export const title = ref(document.title);
export const link = ref(document.location.href);
export const linkAlias = ref('');
export const text = ref('');

export const toggleShareModal = async () => {
  const selector = document.querySelector('.swiper-slide-active')
    ? '.swiper-slide-active [data-copy-text]'
    : '[data-copy-text]';

  const element = document.querySelector(selector) as HTMLElement;
  if (element) {
    text.value =
      document.head
        .querySelector('meta[name="description"]')
        ?.getAttribute('content') || '';
    linkAlias.value =
      (document.querySelector('[data-object-url]') as HTMLElement | null)
        ?.dataset.objectUrl || '';
  }

  if ('gtag' in window) {
    window.gtag('event', 'share');
  }

  if ('share' in navigator) {
    const shareData = {
      title: title.value,
      text: text.value,
      url: link.value,
    };

    try {
      await (navigator as any).share(shareData);
    } catch (error) {
      captureException(error);
    }
  } else {
    shareModalVisible.value = !shareModalVisible.value;
    lockScrolling(shareModalVisible.value);
  }
};

export const shareImage = async (url: string) => {
  if (!url) {
    return;
  }
  // Use native share dialog if supported.
  if (navigator.share) {
    // Fetch the image
    const response = await fetch(url);
    const blob = await response.blob();

    const filesArray = [
      new File([blob], 'file.jpg', {
        type: 'image/jpeg',
        lastModified: new Date().getTime(),
      }),
    ];
    const shareData = {
      files: filesArray,
    };
    navigator.share(shareData);
    return;
  }

  // Otherwise copy image to clipboard
  const labels = {
    en: {
      successTitle: 'Share image',
      message: 'Image copied to your clipboard.',
    },
    de: {
      successTitle: 'Bild teilen',
      message: 'Bild wurde in die Zwischenablage kopiert.',
    },
  } as const;
  const imageUrl = url;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  // Fetch the image
  const response = await fetch(imageUrl);
  const blob = await response.blob();
  const img = new Image();

  img.onload = () => {
    if (!ctx) {
      return;
    }

    // Set canvas dimensions to match the image
    canvas.width = img.width;
    canvas.height = img.height;

    // Draw the image on the canvas
    ctx.drawImage(img, 0, 0);

    // Convert the canvas to a Blob
    canvas.toBlob(async (canvasBlob) => {
      if (canvasBlob) {
        // Create a ClipboardItem from the Blob
        const clipboardItem = new ClipboardItem({
          'image/png': canvasBlob,
        });

        // Write the ClipboardItem to the clipboard
        try {
          await navigator.clipboard.write([clipboardItem]);
          showNotification(
            labels[lang].successTitle,
            labels[lang].message,
            'copy'
          );
          setTimeout(() => {
            hideNotification();
          }, 10000);
        } catch (err) {}
      }
    }, 'image/png');
  };

  // Set the image source to trigger loading
  img.src = URL.createObjectURL(blob);
};
